/**
 * WorkspacePicker — choose a directory on the agent host for
 * claude_code / codex workspaces.
 *
 * Expandable tree backed by the agent's `list_directory` command,
 * proxied through cloud's POST /api/v1/agents/:agentId/list-directory
 * endpoint. Quick locations (the agent's common_paths reply) are
 * surfaced as chips for fast access to ~/Documents/GitHub, etc.
 *
 * Each tree node lazy-loads its children on expand. Results cache per
 * path in component state for the lifetime of the modal (no refresh
 * cycle in v1 — close + reopen the picker if you need a refresh).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';

interface DirEntry {
  name: string;
  is_dir: boolean;
  is_git_repo?: boolean;
}

interface ListDirResponse {
  path: string;
  parent: string;
  entries: DirEntry[];
  home: string;
  common_paths: string[];
}

interface WorkspacePickerProps {
  agentId: string;
  value: string;
  onChange: (path: string) => void;
  disabled?: boolean;
}

export default function WorkspacePicker({ agentId, value, onChange, disabled }: WorkspacePickerProps) {
  const { getHeaders } = useApiHeaders();
  const [rootListing, setRootListing] = useState<ListDirResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Children cache: path → its directory entries.
  const [children, setChildren] = useState<Record<string, DirEntry[]>>({});
  // Loading state per path so we can show spinners on click.
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  // Expanded set so a node remembers its open state during navigation.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // In-flight request set — guards against double-fetch when two render
  // cycles race on the same path. ref so we don't trigger re-renders.
  const inFlight = useRef<Set<string>>(new Set());
  // Cache ref mirrors children state for synchronous reads in the
  // toggleExpand callback (state reads in callbacks are stale).
  const childrenRef = useRef<Record<string, DirEntry[]>>({});

  const fetchListing = useCallback(async (path?: string): Promise<ListDirResponse | null> => {
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_URL}/api/v1/agents/${agentId}/list-directory`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: path ?? undefined, show_hidden: false }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error || `list-directory failed: ${res.status}`);
      }
      return await res.json() as ListDirResponse;
    } catch (e: any) {
      setError(String(e?.message ?? e));
      return null;
    }
  }, [agentId, getHeaders]);

  // Load HOME on mount as the root.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await fetchListing();
      if (!cancelled && r) {
        setRootListing(r);
        // Seed the children cache so the root expands instantly.
        childrenRef.current[r.path] = r.entries;
        setChildren(prev => ({ ...prev, [r.path]: r.entries }));
        setExpanded(prev => new Set(prev).add(r.path));
      }
    })();
    return () => { cancelled = true; };
  }, [fetchListing]);

  const toggleExpand = useCallback(async (path: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
    // Lazy-fetch children if we don't have them yet AND no request is
    // already in flight. Cache + in-flight checks use refs so we can't
    // race on stale state.
    if (childrenRef.current[path] || inFlight.current.has(path)) return;
    inFlight.current.add(path);
    setLoading(prev => ({ ...prev, [path]: true }));
    try {
      const r = await fetchListing(path);
      if (r) {
        childrenRef.current[path] = r.entries;
        setChildren(prev => ({ ...prev, [path]: r.entries }));
      }
    } finally {
      inFlight.current.delete(path);
      setLoading(prev => ({ ...prev, [path]: false }));
    }
  }, [fetchListing]);

  const jumpTo = useCallback(async (path: string) => {
    setError(null);
    // If we already have this path cached, skip the network round-trip
    // and just rebuild the listing locally. Saves a request when the
    // user clicks ↑ Up to a parent they came from.
    if (childrenRef.current[path]) {
      setRootListing(prev => prev && { ...prev, path, parent: parentOf(path), entries: childrenRef.current[path] });
      setExpanded(prev => new Set(prev).add(path));
      return;
    }
    const r = await fetchListing(path);
    if (r) {
      childrenRef.current[r.path] = r.entries;
      setRootListing(r);
      setChildren(prev => ({ ...prev, [r.path]: r.entries }));
      setExpanded(prev => new Set(prev).add(r.path));
    }
  }, [fetchListing]);

  // parentOf computes /foo/bar/baz → /foo/bar without a network call.
  function parentOf(p: string): string {
    const i = p.lastIndexOf('/');
    return i <= 0 ? '/' : p.slice(0, i);
  }

  const quickLocations = useMemo(() => rootListing?.common_paths ?? [], [rootListing]);

  return (
    <div className="space-y-2">
      {/* Selected-path display + manual edit */}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="/Users/you/projects/my-repo"
        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
        disabled={disabled}
        spellCheck={false}
      />

      {/* Quick locations */}
      {quickLocations.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {quickLocations.map(p => (
            <button
              key={p}
              type="button"
              onClick={() => { onChange(p); void jumpTo(p); }}
              disabled={disabled}
              className="px-2.5 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer font-mono"
            >
              {p.replace(rootListing?.home ?? '~~~', '~')}
            </button>
          ))}
        </div>
      )}

      {/* Up button + current path */}
      {rootListing && (
        <div className="flex items-center space-x-2 text-xs text-slate-500 dark:text-slate-400">
          <button
            type="button"
            onClick={() => void jumpTo(rootListing.parent)}
            disabled={disabled || rootListing.parent === rootListing.path}
            className="px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            ↑ Up
          </button>
          <span className="font-mono truncate">{rootListing.path}</span>
        </div>
      )}

      {/* Tree */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 h-96 max-h-[50vh] overflow-y-auto overscroll-contain">
        {error && (
          <div className="p-3 text-xs text-red-600 dark:text-red-400">{error}</div>
        )}
        {rootListing ? (
          <TreeNode
            path={rootListing.path}
            name={rootListing.path === rootListing.home ? '~' : rootListing.path.split('/').pop() || '/'}
            isDir
            depth={0}
            expanded={expanded}
            children_={children}
            loading={loading}
            selected={value}
            onSelect={onChange}
            onToggle={toggleExpand}
            disabled={disabled}
          />
        ) : (
          !error && <div className="p-3 text-xs text-slate-400 dark:text-slate-600">Loading…</div>
        )}
      </div>

      <p className="text-xs text-slate-400 dark:text-slate-500">
        Click a directory name to select it. Click ▶ to expand without selecting.
      </p>
    </div>
  );
}

function TreeNode({
  path,
  name,
  isDir,
  depth,
  expanded,
  children_,
  loading,
  selected,
  onSelect,
  onToggle,
  disabled,
}: {
  path: string;
  name: string;
  isDir: boolean;
  depth: number;
  expanded: Set<string>;
  children_: Record<string, DirEntry[]>;
  loading: Record<string, boolean>;
  selected: string;
  onSelect: (p: string) => void;
  onToggle: (p: string) => void;
  disabled?: boolean;
}) {
  const isExpanded = expanded.has(path);
  const kids = children_[path];
  const isLoading = loading[path];
  const isSelected = selected === path;

  return (
    <div className="text-sm">
      <div
        className={`flex items-center px-2 py-1 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/60 ${
          isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {isDir && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(path); }}
            disabled={disabled}
            className="mr-1 text-slate-400 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-300 w-4"
          >
            {isLoading ? '…' : isExpanded ? '▼' : '▶'}
          </button>
        )}
        <button
          type="button"
          onClick={() => isDir && onSelect(path)}
          disabled={disabled || !isDir}
          className={`flex-1 text-left truncate ${isSelected ? 'font-semibold text-primary-700 dark:text-primary-300' : 'text-slate-700 dark:text-slate-300'}`}
          title={path}
        >
          {isDir ? '📁' : '📄'} {name}
        </button>
      </div>
      {isDir && isExpanded && kids && (
        <div>
          {kids.length === 0 ? (
            <div className="text-xs text-slate-400 dark:text-slate-600" style={{ paddingLeft: `${depth * 16 + 32}px` }}>
              (empty)
            </div>
          ) : (
            kids.filter(k => k.is_dir).map(k => (
              <TreeNode
                key={`${path}/${k.name}`}
                path={`${path}/${k.name}`}
                name={k.name + (k.is_git_repo ? ' ●' : '')}
                isDir={k.is_dir}
                depth={depth + 1}
                expanded={expanded}
                children_={children_}
                loading={loading}
                selected={selected}
                onSelect={onSelect}
                onToggle={onToggle}
                disabled={disabled}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
